import React, { useEffect, useState, useRef, useCallback } from 'react';
import './UserProfile.css';
import { buildApiUrl } from '../config/api';
import { CacheManager } from '../utils/cacheManager';
import { 
  ACTIVITY_CALENDAR_COLORS, 
  getActivityColor, 
  isToday, 
  formatActivityDate, 
  WEEK_DAYS, 
  MONTHS, 
  getMonth, 
  groupByWeekMondayStart,
  getCalendarDayStyle,
  calculateMonthLabelsByFirstMonday
} from './ActivityCalendar';

interface UserProfileProps {
  user: {
    id: number;
    username: string;
    email?: string;
    created_at: string;
  } | null;
  onSettingsClick: () => void;
  onLogout: () => void;
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>;
  currentTheme?: string;
}

interface ActivityDay {
  date: string; // YYYY-MM-DD
  hours: number;
}



const UserProfile: React.FC<UserProfileProps> = ({ user, onSettingsClick, onLogout, authenticatedFetch, currentTheme = 'modern' }) => {
  const [consent, setConsent] = useState(false);
  const [location, setLocation] = useState<{latitude: number, longitude: number} | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [weeks, setWeeks] = useState<ActivityDay[][]>([]);
  const [monthLabels, setMonthLabels] = useState<string[]>([]);
  const [max, setMax] = useState(1);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(false);
  const calendarFetchingRef = useRef(false); // 防止重复请求
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null); // 新增：toast定时器引用

  // 检测是否为暗色主题
  const isDarkTheme = currentTheme === 'dark';

  // 新增：显示toast的通用函数
  const showToast = useCallback((message: string, duration: number = 3000) => {
    // 清除之前的定时器
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    // 设置新的toast
    setToast(message);
    
    // 设置自动隐藏定时器
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
      toastTimeoutRef.current = null;
    }, duration);
  }, []);

  // 新增：清除toast的函数
  const clearToast = useCallback(() => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
    setToast(null);
  }, []);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  // 滚动到最右侧的函数
  const scrollToRight = useCallback(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current;
      const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
      scrollElement.scrollLeft = maxScrollLeft;
    }
  }, []);



  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked);
    if (e.target.checked) {
      getLocation();
    }
  };

  // 处理升级按钮点击
  const handleUpgrade = async () => {
    if (isUpgrading) return; // 防止重复点击
    
    setIsUpgrading(true);
    try {
      // 显示确认对话框
      const confirmed = window.confirm(
        '确定要清除所有本地缓存并重新加载最新代码吗？\n\n这将：\n• 清除所有本地存储数据\n• 清除浏览器缓存\n• 重新加载页面\n\n注意：您需要重新登录。'
      );
      
      if (confirmed) {
        // 显示升级提示
        showToast('正在清除缓存并升级...');
        
        // 清除所有缓存
        await CacheManager.clearAllCache();
        
        // 显示成功提示
        showToast('缓存清除完成，正在重新加载...');
        
        // 延迟一下让用户看到提示，然后重新加载页面
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('升级失败:', error);
      showToast('升级失败，请重试');
    } finally {
      setIsUpgrading(false);
    }
  };

  // 处理日历格子点击
  const handleDayClick = (day: ActivityDay, event: React.MouseEvent | React.TouchEvent) => {
    console.log('=== 日历格子点击事件触发 ===');
    console.log('事件类型:', event.type);
    console.log('点击的日期:', day.date);
    console.log('点击的时长:', day.hours);
    
    // 移除 preventDefault() 以避免被动事件监听器错误
    // 只使用 stopPropagation() 阻止事件冒泡
    event.stopPropagation();
    
    if (day.date) {
      console.log('显示toast提示');
      
      // 显示toast提示工作时长
      showToast(`${formatActivityDate(day.date)} - 工作时长: ${day.hours}小时`, 3000);
    } else {
      console.log('没有日期，不显示提示');
    }
  };





  // 获取定位
  const getLocation = () => {
    if (!navigator.geolocation) {
      showToast('您的浏览器不支持定位功能');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        showToast('定位成功！');
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          showToast('用户拒绝了位置请求');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          showToast('位置信息不可用');
        } else if (error.code === error.TIMEOUT) {
          showToast('定位超时，请重试');
        } else {
          showToast('定位失败');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  // 获取活动日历数据
  const fetchActivityCalendar = async () => {
    // 防止重复请求
    if (calendarFetchingRef.current) {
      return;
    }
    calendarFetchingRef.current = true;
    setLoadingCalendar(true);
    
    try {
      // 计算最近半年日期
      const today = new Date();
      const endStr = today.toISOString().slice(0, 10);
      const startDate = new Date(today.getTime() - 181 * 24 * 60 * 60 * 1000);
      const startStr = startDate.toISOString().slice(0, 10);
      
      const fetchFunction = authenticatedFetch || fetch;
      const url = authenticatedFetch 
        ? `/api/me/activity_calendar?start=${startStr}&end=${endStr}`
        : `https://xiongsonglin.top/api/me/activity_calendar?start=${startStr}&end=${endStr}`;
      
      const response = await fetchFunction(url);
      
      if (response.ok) {
        const data = await response.json();
        setMax(Math.max(1, ...data.map((d: ActivityDay) => d.hours)));
        const weekArr = groupByWeekMondayStart(data, startStr, endStr);
        setWeeks(weekArr);
        
        // 使用新的月份标签计算函数，让每个月对齐该月的第一个周一
        const labels = calculateMonthLabelsByFirstMonday(weekArr, startStr, endStr);
        setMonthLabels(labels);
      } else {
        console.error('获取活动日历失败');
      }
    } catch (error) {
      console.error('获取活动日历失败:', error);
    } finally {
      setLoadingCalendar(false);
      calendarFetchingRef.current = false;
    }
  };

  useEffect(() => {
    // 使用传入的authenticatedFetch函数，如果没有则使用默认的fetch
    const fetchUserInfo = async () => {
      // 防止重复请求
      if (fetchingRef.current) {
        return;
      }
      fetchingRef.current = true;
      
      try {
        const fetchFunction = authenticatedFetch || fetch;
        // 如果使用authenticatedFetch，传入相对路径；否则使用完整URL
        const url = authenticatedFetch ? '/api/me' : buildApiUrl('/api/me');
        const response = await fetchFunction(url);
        
        if (response.ok) {
          // 这里原本有setForm，直接删除即可
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      } finally {
        fetchingRef.current = false;
      }
    };
    
    fetchUserInfo();
    // 获取活动日历数据
    fetchActivityCalendar();
  }, [authenticatedFetch]);

  // 初始时自动滚动到最右侧
  useEffect(() => {
    if (weeks.length > 0) {
      // 使用 setTimeout 确保DOM完全渲染后再滚动
      const timer = setTimeout(() => {
        scrollToRight();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [weeks, monthLabels, scrollToRight]);

  // 数据加载完成后再次确保滚动到最右侧
  useEffect(() => {
    if (!loadingCalendar && weeks.length > 0) {
      const timer = setTimeout(() => {
        scrollToRight();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [loadingCalendar, weeks.length, scrollToRight]);



  if (!user) {
    return (
      <div className="user-profile">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载用户信息中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile">
      <div className="profile-header">
        <div className="profile-avatar">
          <span className="avatar-text">{user.username.charAt(0).toUpperCase()}</span>
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{user.username}</h2>
          <p className="profile-email">{user.email || '未设置邮箱'}</p>
          <p className="profile-join-date">加入时间: {new Date(user.created_at).toLocaleDateString()}</p>
        </div>
        <div className="profile-actions">
          <button className="settings-button" onClick={onSettingsClick}>
            ⚙️ 设置
          </button>
          <button 
            className="upgrade-button" 
            onClick={handleUpgrade}
            disabled={isUpgrading}
          >
            {isUpgrading ? '🔄 升级中...' : '🚀 升级'}
          </button>
          <button className="logout-button" onClick={onLogout}>
            🚪 登出
          </button>
        </div>
      </div>
      
      <div className="profile-stats">
        <div className="stat-item">
          <div className="stat-number">6</div>
          <div className="stat-label">进行中项目</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">12</div>
          <div className="stat-label">总投入时间</div>
        </div>
        <div className="stat-item">
          <div className="stat-number">85%</div>
          <div className="stat-label">平均完成度</div>
        </div>
      </div>

      {/* 活动日历区域 */}
      <div className="activity-calendar-section">
        <h3 className="calendar-title">活动记录</h3>
        {loadingCalendar ? (
          <div className="loading-calendar">
            <div className="loading-spinner"></div>
            <span>加载活动记录中...</span>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {/* 左侧星期标签，显示完整的周几 */}
              <div style={{ display: 'flex', flexDirection: 'column', marginRight: 4, paddingTop: 13 }}>
                {WEEK_DAYS.map((d, i) => (
                  <div
                    key={i}
                                      style={{
                    height: 12.2, // 调整到平均值字高
                    fontSize: 9, // 调整到平均值字体大小
                    color: '#888',
                    lineHeight: '12.2px', // 调整到平均值行高
                    textAlign: 'right',
                    width: 14, // 调整到平均值宽度
                    boxSizing: 'border-box',
                    marginTop: i === 0 ? 0 : 2 // 调整到平均值间距
                  }}
                  >
                    {d}
                  </div>
                ))}
              </div>
              {/* 主体内容区域 - 包含月份标签和格子，一起滚动 */}
              <div
                ref={scrollRef}
                style={{ 
                  overflowX: 'auto', 
                  overflowY: 'hidden',
                  maxWidth: '240px', // 使用更小的固定宽度，确保不超出容器
                  WebkitOverflowScrolling: 'touch'
                }}>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 'max-content' }}>
                  {/* 顶部月份标签 */}
                  <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 2 }}>
                                      {monthLabels.map((label, idx) => (
                    <div key={idx} style={{ width: 14, textAlign: 'center', fontSize: 10, color: '#888' }}>{label}</div>
                  ))}
                  </div>
                  {/* 主体格子 */}
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                    {weeks.map((week, colIdx) => (
                      <div key={colIdx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {week.map((d, rowIdx) => (
                          <div
                            key={rowIdx}
                            className={isToday(d.date) ? 'calendar-day today' : 'calendar-day'}
                            style={getCalendarDayStyle(d, max, isDarkTheme, 8, 1)}
                            onClick={(e) => {
                              console.log('onClick 触发，日期:', d.date);
                              handleDayClick(d, e);
                            }}
                            onTouchStart={(e) => {
                              console.log('onTouchStart 触发，日期:', d.date);
                              // 添加触摸反馈
                              const target = e.currentTarget as HTMLElement;
                              target.style.transform = 'scale(0.95)';
                              target.style.boxShadow = '0 0 8px rgba(79, 209, 199, 0.3)';
                              
                              // 恢复原状
                              setTimeout(() => {
                                target.style.transform = '';
                                target.style.boxShadow = '';
                              }, 150);
                              
                              handleDayClick(d, e);
                            }}
                            title={d.date ? `${formatActivityDate(d.date)} - ${d.hours}小时` : ''}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 新增定位同意复选框 */}
      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <label style={{ fontSize: 16 }}>
          <input
            type="checkbox"
            checked={consent}
            onChange={handleConsentChange}
            style={{ marginRight: 8 }}
          />
          是否同意获取定位
        </label>
        {location && (
          <div style={{ marginTop: 8, color: '#333' }}>
            当前定位：{location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
          </div>
        )}
        {toast && (
          <div
            style={{
              position: 'fixed',
              top: '50vh',
              left: '50vw',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(51, 51, 51, 0.95)',
              color: '#fff',
              padding: '18px 28px',
              borderRadius: '16px',
              zIndex: 9999,
              fontSize: '16px',
              fontWeight: '500',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
              maxWidth: '90%',
              textAlign: 'center',
              whiteSpace: 'nowrap',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile; 